import { useState, useEffect, useRef } from 'react';
import nangluccotloi1 from '../../../assets/images/nangluccotloi1.jpg';
import nangluccotloi2 from '../../../assets/images/nangluccotloi2.jpg';
import nangluccotloi3 from '../../../assets/images/nangluccotloi3.jpg';
import nangluccotloi4 from '../../../assets/images/nangluccotloi4.jpg';

export default function CoreCapabilities() {
  const [activeTab, setActiveTab] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setIsVisible(true);
        });
      },
      { threshold: 0.1 }
    );
    const el = sectionRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, []);

  const capabilities = [
    {
      id: 0,
      icon: 'ri-compass-3-line',
      title: 'Tư vấn chiến lược & giải pháp',
      color: '#06b6d4',
      features: [
        'Đánh giá hiện trạng và mức độ sẵn sàng chuyển đổi số',
        'Xây dựng lộ trình và kiến trúc tổng thể hệ thống',
        'Định hướng giải pháp phù hợp mục tiêu quản lý và vận hành'
      ],
      image: nangluccotloi1
    },
    {
      id: 1,
      icon: 'ri-eye-line',
      title: 'Giám sát & tư vấn triển khai',
      color: '#00c897',
      features: [
        'Giám sát độc lập tiến độ và chất lượng triển khai',
        'Đảm bảo tuân thủ thiết kế, tiêu chuẩn kỹ thuật',
        'Kiểm soát rủi ro và hỗ trợ điều chỉnh kịp thời'
      ],
      image: nangluccotloi2
    },
    {
      id: 2,
      icon: 'ri-server-line',
      title: 'Tư vấn hạ tầng công nghệ',
      color: '#2a4b7c',
      features: [
        'Kiến trúc hạ tầng CNTT, Cloud & Hybrid Cloud',
        'Tối ưu hiệu năng, chi phí và khả năng mở rộng',
        'Đảm bảo tính sẵn sàng và ổn định hệ thống'
      ],
      image: nangluccotloi3
    },
    {
      id: 3,
      icon: 'ri-shield-check-line',
      title: 'Tư vấn bảo mật & an toàn thông tin',
      color: '#2ecc71',
      features: [
        'Định hướng áp dụng tiêu chuẩn, quy chuẩn an toàn thông tin',
        'Đánh giá rủi ro và mức độ an toàn hệ thống',
        'Xây dựng chính sách và khung quản trị bảo mật'
      ],
      image: nangluccotloi4
    }
  ];

  return (
    <section
      ref={sectionRef}
      id="capabilities-section"
      className="relative overflow-x-hidden py-12 sm:py-16 md:py-20 lg:py-24"
      style={{
        backgroundImage: 'linear-gradient(to right bottom, #ffffff, #f0f9ff, #e0f2fe)',
      }}
    >
      {/* Background overlay (optional) */}
      <div className="absolute top-0 right-0 w-1/2 md:w-1/3 h-full bg-gradient-to-l from-[#06b6d433] to-transparent opacity-20 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header – điểm nhấn: fade + slide xuống */}
        <div
          className={`max-w-3xl mx-auto text-center mb-10 md:mb-16 lg:mb-20 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-[#06b6d433] rounded-full mb-4 sm:mb-6">
            <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center">
              <i className="ri-star-line text-[#06b6d4] text-base sm:text-lg"></i>
            </div>
            <span className="text-xs sm:text-sm font-semibold text-[#2a4b7c]">Năng lực cốt lõi</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#2a4b7c] mb-4 sm:mb-6 px-1">
            Dịch vụ tư vấn chuyên sâu
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed px-1">
            Chúng tôi cung cấp các dịch vụ tư vấn toàn diện, từ chiến lược đến triển khai và vận hành
          </p>
        </div>

        {/* Tab Navigation – grid 1 cột mobile, 2 cột tablet, flex wrap desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap lg:justify-center gap-3 sm:gap-4 mb-10 md:mb-16 max-w-4xl mx-auto lg:max-w-none">
          {capabilities.map((cap, index) => (
            <div
              key={cap.id}
              className={`transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: `${100 + index * 80}ms` }}
            >
              <button
                onClick={() => setActiveTab(cap.id)}
                className={`group w-full sm:w-auto px-4 py-3 sm:px-6 sm:py-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer text-left sm:text-center ${
                  activeTab === cap.id
                    ? 'bg-[#2a4b7c] text-white shadow-xl sm:scale-105'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <div className="flex items-center gap-3 justify-center sm:justify-center">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center flex-shrink-0">
                    <i className={`${cap.icon} text-lg sm:text-xl ${activeTab === cap.id ? 'text-white' : 'text-gray-500'}`}></i>
                  </div>
                  <span className="text-xs sm:text-sm line-clamp-2 sm:line-clamp-1">{cap.title}</span>
                </div>
              </button>
            </div>
          ))}
        </div>

        {/* Tab Content – wrapper scale + fade (điểm nhấn), rồi ảnh trái / nội dung phải slide */}
        <div
          className={`transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-[0.98]'
          }`}
        >
          <div className="relative min-h-[280px] sm:min-h-[320px] md:min-h-0">
            {capabilities.map((cap) => (
              <div
                key={cap.id}
                className={`transition-all duration-500 ${
                  activeTab === cap.id
                    ? 'opacity-100 relative'
                    : 'opacity-0 absolute inset-0 pointer-events-none'
                }`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 lg:gap-12 items-center">
                  {/* Image – slide từ trái */}
                  <div
                    className={`relative order-2 lg:order-1 transition-all duration-1000 delay-400 ${
                      isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
                    }`}
                  >
                    <div className="relative z-10 rounded-xl md:rounded-2xl overflow-hidden shadow-xl md:shadow-2xl">
                      <img
                        src={cap.image}
                        alt={cap.title}
                        className="w-full h-auto max-h-[280px] sm:max-h-[320px] lg:max-h-none object-cover lg:object-contain"
                      />
                    </div>
                    <div
                      className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-6 w-3/4 h-3/4 md:w-full md:h-full rounded-2xl opacity-20 blur-2xl pointer-events-none"
                      style={{ backgroundColor: cap.color }}
                    />
                  </div>

                  {/* Content – slide từ phải + feature list stagger */}
                  <div
                    className={`space-y-5 md:space-y-8 order-1 lg:order-2 transition-all duration-1000 delay-500 ${
                      isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
                    }`}
                  >
                    <div>
                      <div
                        className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-xl md:rounded-2xl mb-4 md:mb-6"
                        style={{ backgroundColor: `${cap.color}20` }}
                      >
                        <i className={`${cap.icon} text-2xl sm:text-3xl md:text-4xl`} style={{ color: cap.color }} />
                      </div>
                      <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#2a4b7c] mb-3 md:mb-4">
                        {cap.title}
                      </h3>
                    </div>

                    <div className="space-y-3 md:space-y-4">
                      {cap.features.map((feature, idx) => (
                        <div
                          key={idx}
                          className={`flex items-start gap-3 md:gap-4 group transition-all duration-500 ${
                            isVisible && activeTab === cap.id
                              ? 'opacity-100 translate-y-0'
                              : 'opacity-0 translate-y-3'
                          }`}
                          style={{
                            transitionDelay:
                              isVisible && activeTab === cap.id ? `${600 + idx * 60}ms` : '0ms',
                          }}
                        >
                          <div
                            className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 md:mt-1"
                            style={{ backgroundColor: `${cap.color}20` }}
                          >
                            <i className="ri-check-line text-xs md:text-sm" style={{ color: cap.color }} />
                          </div>
                          <p className="text-sm sm:text-base text-gray-700 leading-relaxed group-hover:text-[#2a4b7c] transition-colors">
                            {feature}
                          </p>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={() => {
                        const contactSection = document.getElementById('contact-section');
                        contactSection?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="inline-flex items-center gap-2 px-5 py-3 sm:px-6 sm:py-3.5 md:px-8 md:py-4 bg-[#2a4b7c] text-white text-sm sm:text-base rounded-lg font-semibold hover:bg-[#1e3557] hover:shadow-xl transition-all duration-300 cursor-pointer"
                    >
                      Tìm hiểu chi tiết
                      <i className="ri-arrow-right-line" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
